import React, { useState, useEffect, useMemo } from 'react';
import validator from 'validator';
import { useLayoutContext } from '../../providers/LayoutProvider';
import { useValidateInput } from '../../shared/useValidateInput';
import {
    KiteCheckbox,
    KiteIcon,
    KiteSelect,
    KiteTextInput,
} from '@kite/react-kite';
import {
    KITE_INPUT_TEXT_TYPES,
    FORM_KITE_ELEMENTS_ENUM,
    IFormItems,
    IDynamicFormInputErrors,
} from './IDynamicFormTypes';

export const DyamicFormElements = (
    formItems: IFormItems[],
    formStage: number,
    errorFlag: boolean,
    staticFlag: boolean,
    setErrorFlag: React.Dispatch<React.SetStateAction<boolean>>
) => {
    const { inputProspect, setInputProspect } = useLayoutContext();
    const [selectedOptions, setSelectedOptions] = useState({});
    const [selectValues, setSelectValues] = useState({});
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const reqProspectFilter = [];
    const [inputErrors, setInputErrors] = useState<IDynamicFormInputErrors>({});

    const handleTextChange = (event) => {
        const thisInput = event.target;
        const sanitizedValue =
            thisInput.name === `email`
                ? validator.normalizeEmail(thisInput.value)
                : validator.blacklist(thisInput.value, `/[[]<>*?]/`);
        setInputProspect({
            ...inputProspect,
            [thisInput.name]: sanitizedValue,
        });
        useValidateInput(
            thisInput.name,
            inputErrors,
            setInputErrors,
            sanitizedValue
        );
    };

    const handleBlur = (event) => {
        const thisInput = event.target;
        useValidateInput(
            thisInput.name,
            inputErrors,
            setInputErrors,
            thisInput.value
        );
    };

    const handleSelectBlur = (event) => {
        const thisInput = event.target;
        useValidateInput(
            thisInput.name,
            inputErrors,
            setInputErrors,
            thisInput.value,
            thisInput.tagName,
            thisInput.multiple
        );
    };

    const handleMultiSelectOnChange = (event) => {
        const thisInput = event.target;
        const selectedOptions = Array.from(
            event.target.selectedOptions,
            (option: HTMLOptionElement) => option.value
        );
        setSelectedOptions((prevVals) => ({
            ...prevVals,
            [thisInput.id]: selectedOptions,
        }));
        setInputProspect({
            ...inputProspect,
            [event.target.name]: selectedOptions,
        });
        useValidateInput(
            thisInput.name,
            inputErrors,
            setInputErrors,
            thisInput.value,
            thisInput.tagName,
            true
        );
    };

    const handleSelectOnChange = (event) => {
        const thisInput = event.target;
        const selectedValue = thisInput.value;
        setSelectValues((prevVals) => ({
            ...prevVals,
            [thisInput.id]: selectedValue,
        }));
        setInputProspect({
            ...inputProspect,
            [thisInput.name]: selectedValue,
        });
        useValidateInput(
            thisInput.name,
            inputErrors,
            setInputErrors,
            selectedValue,
            thisInput.tagName
        );
    };

    const checkboxHandler = (event) => {
        if (!isChecked) {
            setIsChecked(true);
            if (event.target.required) {
                setErrorFlag(false);
            }
            setInputProspect({
                ...inputProspect,
                [event.target.name]: true,
            });
        } else {
            setIsChecked(false);
            if (event.target.required) {
                setErrorFlag(true);
            }
            setInputProspect({
                ...inputProspect,
                [event.target.name]: false,
            });
        }
    };

    useEffect(() => {
        const checkInputValues = () =>
            reqProspectFilter.every((key) => inputProspect[key]); //compares every reqProspectFilter[key] against the inputProspect entry to find missing values

        const checkInputErrors = () =>
            reqProspectFilter.some((key) => inputErrors[key]); //checks for the first missing value in inputErrors - no values == no errors

        if (checkInputValues() && !checkInputErrors()) {
            setErrorFlag(false);
        } else {
            setErrorFlag(true);
        }
    }, [reqProspectFilter, inputProspect, inputErrors, setErrorFlag]);

    const formElements: JSX.Element[] = Object.values(formItems)
        .filter((fItem) => {
            const { required, inputStage, name, type } = fItem;

            if (
                inputStage === formStage ||
                name === `email_address` ||
                type === `hidden`
            ) {
                if (required) {
                    reqProspectFilter.push(name); //initialize filter based on rendered inputs - to be used for validation (which inputs need to be validated vs. which inputs are optional like Comments)
                }
                return fItem;
            }
        })
        .map((fItem, idx) => {
            if (fItem.type === `hidden`) {
                return (
                    <div className="visually-hidden hidden" hidden>
                        <label
                            htmlFor={fItem.id}
                        >{`${fItem.formItemName}`}</label>
                        <input
                            type={`text`}
                            name={`${fItem.id}`}
                            id={fItem.id}
                            className={`visually-hidden hidden`}
                            value={`${fItem.defaultValue}`}
                            hidden
                            disabled
                            tabIndex={-1}
                        />
                    </div>
                );
            }
            if (fItem.kiteComponent === `KiteTextInput`) {
                return (
                    <React.Fragment key={idx}>
                        <KiteTextInput
                            id={fItem.id}
                            label={fItem.formItemName}
                            name={fItem.name}
                            type={`${fItem.type as KITE_INPUT_TEXT_TYPES}`}
                            onChange={handleTextChange.bind(fItem)}
                            onBlur={handleBlur.bind(fItem)}
                            required={fItem.required || false}
                            errorMessage={inputErrors[fItem.name]}
                        />
                    </React.Fragment>
                );
            }
            if (fItem.kiteComponent === `KiteSelect`) {
                if (fItem.multiple) {
                    return (
                        <React.Fragment key={idx}>
                            <div className="kite-form-control kite-form-group">
                                <div className="rk-select-label-tooltip">
                                    <label>{`${fItem.formItemName} (Ctrl-Select or Cmd-Select for multiple)`}</label>
                                </div>
                                <div className="multiselect">
                                    <select
                                        key={idx}
                                        id={fItem.id}
                                        name={`${fItem.name}`}
                                        multiple={true}
                                        className={`${
                                            inputErrors[fItem.name]
                                                ? `error-state`
                                                : ``
                                        }`}
                                        onChange={(e) =>
                                            handleMultiSelectOnChange(e)
                                        }
                                        onBlur={handleSelectBlur.bind(fItem)}
                                        value={selectedOptions[fItem.id] || ``}
                                        required={fItem.required || false}
                                    >
                                        {fItem.inputOptions}
                                    </select>
                                </div>
                                {inputErrors[fItem.name] && (
                                    <span
                                        className="kite-form-control__error"
                                        id={`${fItem.id}-error`}
                                    >
                                        {inputErrors[fItem.name]}
                                    </span>
                                )}
                            </div>
                        </React.Fragment>
                    );
                }
                return (
                    <React.Fragment key={idx}>
                        <KiteSelect
                            id={fItem.id}
                            label={fItem.formItemName}
                            placeholder={` `}
                            name={fItem.name}
                            className={`${
                                inputErrors[fItem.name] ? `error-state` : ``
                            }`}
                            onChange={(e) => handleSelectOnChange(e)}
                            onBlur={handleSelectBlur.bind(fItem)}
                            value={selectValues[fItem.id] || ``}
                            required={fItem.required || false}
                            errorMessage={inputErrors[fItem.name]}
                        >
                            {fItem.inputOptions}
                        </KiteSelect>
                    </React.Fragment>
                );
            }
            if (fItem.kiteComponent === `KiteCheckbox`) {
                return (
                    <React.Fragment key={idx}>
                        <KiteCheckbox
                            key={idx}
                            id={fItem.id}
                            label={fItem.formItemName}
                            name={fItem.name}
                            onChange={checkboxHandler}
                            checked={isChecked}
                            required={fItem.required || false}
                        />
                    </React.Fragment>
                );
            }
        });

    return formElements;
};
